/* Jnorss wishlist
 *
 * Saved products live in the browser (localStorage, key `jnorss_wishlist`) as a list of
 * `{ handle, addedAt, size }` entries, newest first (size is the shopper's pick from the
 * heart's size picker, or null). Nothing is written to Shopify yet: a
 * signed-in customer keeps the same browser list, which is why the wishlist page asks
 * guests to log in but cannot promise more than this device for now. Everything that
 * needs the list goes through `window.JnorssWishlist`, so the header count, product
 * cards and the wishlist page always agree.
 *
 * Events: `jnorss:wishlist:change` on `document`, `detail.handles` is the new list.
 * Hooks:  any element with `data-jnorss-wishlist-toggle="<handle>"` becomes a save button
 *         (`aria-pressed` and the `is-saved` class mirror the saved state);
 *         `[data-jnorss-wishlist-count]` shows the count and is hidden at zero;
 *         `[data-jnorss-wishlist-page]` is the wishlist page (see sections/jnorss-wishlist.liquid).
 *
 * "Move to cart" posts to /cart/add.js with the ids of the cart sections on the page and
 * then fires the theme's own `cart:update` event, so the cart drawer, its contents and
 * the header cart count react exactly as they do to the product page form.
 */
(function () {
  'use strict';

  var KEY = 'jnorss_wishlist';
  var MAX = 100;

  /* Storage ---------------------------------------------------------------------- */

  function normalize(list) {
    if (!Array.isArray(list)) return [];
    var seen = {};
    var out = [];
    list.forEach(function (item) {
      var handle = typeof item === 'string' ? item : item && item.handle;
      if (typeof handle !== 'string' || !handle || seen[handle]) return;
      seen[handle] = true;
      var addedAt = item && typeof item === 'object' && Number(item.addedAt) > 0 ? Number(item.addedAt) : null;
      var size = item && typeof item === 'object' && typeof item.size === 'string' && item.size ? item.size : null;
      out.push({ handle: handle, addedAt: addedAt, size: size });
    });
    return out;
  }

  function readEntries() {
    try {
      var raw = window.localStorage.getItem(KEY);
      return normalize(raw ? JSON.parse(raw) : []);
    } catch (e) {
      return [];
    }
  }

  function readHandles() {
    return readEntries().map(function (entry) {
      return entry.handle;
    });
  }

  /* `silent` saves without firing the change event (used when only a size changes). */
  function write(entries, silent) {
    entries = normalize(entries).slice(0, MAX);
    try {
      window.localStorage.setItem(KEY, JSON.stringify(entries));
    } catch (e) {
      /* private mode or storage full: the list still works for this page */
    }
    if (silent) return;
    document.dispatchEvent(
      new CustomEvent('jnorss:wishlist:change', {
        detail: {
          handles: entries.map(function (entry) {
            return entry.handle;
          }),
        },
      })
    );
  }

  var Wishlist = {
    key: KEY,
    get: readHandles,
    entries: readEntries,
    count: function () {
      return readEntries().length;
    },
    has: function (handle) {
      return readHandles().indexOf(handle) !== -1;
    },
    add: function (handle, size) {
      if (!handle) return;
      var entries = readEntries().filter(function (entry) {
        return entry.handle !== handle;
      });
      entries.unshift({ handle: handle, addedAt: Date.now(), size: typeof size === 'string' && size ? size : null });
      write(entries);
    },
    sizeOf: function (handle) {
      var hit = readEntries().filter(function (entry) {
        return entry.handle === handle;
      })[0];
      return hit ? hit.size : null;
    },
    setSize: function (handle, size) {
      var entries = readEntries();
      var hit = false;
      entries.forEach(function (entry) {
        if (entry.handle === handle) {
          entry.size = typeof size === 'string' && size ? size : null;
          hit = true;
        }
      });
      if (hit) write(entries, true);
    },
    remove: function (handle) {
      var handles = Array.isArray(handle) ? handle : [handle];
      write(
        readEntries().filter(function (entry) {
          return handles.indexOf(entry.handle) === -1;
        })
      );
    },
    toggle: function (handle, size) {
      if (Wishlist.has(handle)) {
        Wishlist.remove(handle);
        return false;
      }
      Wishlist.add(handle, size);
      return true;
    },
    clear: function () {
      write([]);
    },
  };

  window.JnorssWishlist = Wishlist;

  /* Same smallest-to-largest order the listing's size row uses; unknown values keep the
     product's own order after the known ones. */
  var SIZE_ORDER = 'xxxs,3xs,xxs,2xs,xs,extrasmall,s,small,m,medium,l,large,xl,extralarge,xxl,2xl,xxxl,3xl,xxxxl,4xl,xxxxxl,5xl,xxxxxxl,6xl'.split(',');

  function sizeRank(value) {
    var key = String(value).toLowerCase().replace(/[\s.-]/g, '');
    var index = SIZE_ORDER.indexOf(key);
    return index === -1 ? SIZE_ORDER.length : index;
  }

  function orderedValues(option) {
    return (option.values || [])
      .map(function (value, index) {
        return { value: value, index: index, rank: sizeRank(value) };
      })
      .sort(function (a, b) {
        return a.rank - b.rank || a.index - b.index;
      })
      .map(function (item) {
        return item.value;
      });
  }

  /* Header count and save buttons ------------------------------------------------ */

  function syncUI(list) {
    list = list || readHandles();
    var n = list.length;

    document.querySelectorAll('[data-jnorss-wishlist-count]').forEach(function (el) {
      el.textContent = n > 99 ? '99+' : String(n);
      el.hidden = n === 0;
    });

    document.querySelectorAll('[data-jnorss-wishlist-toggle]').forEach(function (el) {
      var saved = list.indexOf(el.getAttribute('data-jnorss-wishlist-toggle')) !== -1;
      el.setAttribute('aria-pressed', saved ? 'true' : 'false');
      el.classList.toggle('is-saved', saved);
    });
  }

  /* Toast: bottom-right, 3 seconds, after a heart is tapped. */
  var TOAST_MS = 3000;
  var toast = null;
  var toastTimer = 0;

  function wishlistUrl() {
    var link = document.querySelector('[data-jnorss-wishlist-link]');
    return (link && link.getAttribute('href')) || '/pages/wishlist';
  }

  function hideToast() {
    if (toast) toast.classList.remove('is-visible');
  }

  /* kind: true / 'added', false / 'removed', or 'moved' (from the cart). */
  function showToast(kind) {
    if (kind === true) kind = 'added';
    if (kind === false) kind = 'removed';
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'jnorss-wishlist-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.innerHTML =
        '<p class="jnorss-wishlist-toast__text"></p>' +
        '<button type="button" class="jnorss-wishlist-toast__close" aria-label="Close">' +
        '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
        '</button>';
      toast.querySelector('.jnorss-wishlist-toast__close').addEventListener('click', hideToast);
      document.body.appendChild(toast);
    }

    var text = toast.querySelector('.jnorss-wishlist-toast__text');
    text.textContent = '';
    if (kind === 'added' || kind === 'moved') {
      text.appendChild(
        document.createTextNode(
          kind === 'moved' ? 'Item moved to your wishlist, view ' : 'Item has been successfully added to wishlist, view '
        )
      );
      var link = document.createElement('a');
      link.href = wishlistUrl();
      link.textContent = "'My Wishlist'";
      text.appendChild(link);
      text.appendChild(document.createTextNode('.'));
    } else {
      text.textContent = 'Item removed from your wishlist.';
    }

    /* Restart the animation when a second tap lands while the toast is still up. */
    toast.classList.remove('is-visible');
    void toast.offsetWidth;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, TOAST_MS);
  }

  /* Size picker ------------------------------------------------------------------ */

  var picker = null;
  var pickerButton = null;
  var pickerClosedBy = null;

  /* Sizes the Liquid rendered into the heart: [{ v, a, id }], ordered XS -> 2XL. */
  function sizesFor(button) {
    var raw = button.getAttribute('data-jnorss-wishlist-sizes');
    if (!raw) return [];
    var list;
    try {
      list = JSON.parse(raw);
    } catch (e) {
      return [];
    }
    if (!Array.isArray(list)) return [];
    return list
      .filter(function (item) {
        return item && typeof item.v === 'string';
      })
      .map(function (item, index) {
        return { v: item.v, a: !!item.a, id: item.id ? Number(item.id) : null, index: index, rank: sizeRank(item.v) };
      })
      .sort(function (a, b) {
        return a.rank - b.rank || a.index - b.index;
      });
  }

  /* On the product page the shopper has already picked a size: use it instead of asking. */
  function sizeSelectedOnPage(button, sizes) {
    if (!button.classList.contains('jnorss-wishlist-heart--pdp') || !sizes.length) return null;
    var form = document.querySelector('product-form-component form, form[action*="/cart/add"]');
    var idInput = form && form.querySelector('input[name="id"]');
    var id = idInput ? Number(idInput.value) : 0;
    if (id) {
      var byId = sizes.filter(function (size) {
        return size.id === id;
      })[0];
      if (byId) return byId.v;
    }
    var checked = document.querySelector('variant-picker input[type="radio"]:checked, .variant-picker input[type="radio"]:checked');
    if (checked) {
      var byValue = sizes.filter(function (size) {
        return size.v === checked.value;
      })[0];
      if (byValue) return byValue.v;
    }
    return null;
  }

  function closePicker() {
    if (!picker) return;
    picker.remove();
    picker = null;
    pickerButton = null;
    document.removeEventListener('keydown', onPickerKey);
    document.removeEventListener('click', onPickerOutside, true);
  }

  function onPickerKey(event) {
    if (event.key === 'Escape') closePicker();
  }

  function onPickerOutside(event) {
    if (!picker || picker.contains(event.target)) return;
    /* A tap on the same heart closes the picker instead of reopening it. */
    var heart = event.target.closest('[data-jnorss-wishlist-toggle]');
    if (heart && heart === pickerButton) pickerClosedBy = heart;
    closePicker();
  }

  function openPicker(button, handle, sizes) {
    closePicker();
    var isPdp = button.classList.contains('jnorss-wishlist-heart--pdp');
    var host = button.closest('.card-gallery') || button.parentElement;

    picker = document.createElement('div');
    picker.className = 'jnorss-wishlist-picker' + (isPdp ? ' jnorss-wishlist-picker--pdp' : '');
    picker.setAttribute('role', 'dialog');
    picker.setAttribute('aria-label', 'Select size');

    var head = document.createElement('div');
    head.className = 'jnorss-wishlist-picker__head';
    var label = document.createElement('span');
    label.textContent = 'Select size';
    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'jnorss-wishlist-picker__close';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML =
      '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
    close.addEventListener('click', closePicker);
    head.appendChild(label);
    head.appendChild(close);

    var list = document.createElement('div');
    list.className = 'jnorss-wishlist-picker__sizes';
    sizes.forEach(function (size) {
      var option = document.createElement('button');
      option.type = 'button';
      option.className = 'jnorss-wishlist-picker__size' + (size.a ? '' : ' is-sold-out');
      option.textContent = size.v;
      option.disabled = !size.a;
      if (!size.a) option.setAttribute('aria-label', size.v + ' sold out');
      option.addEventListener('click', function () {
        Wishlist.add(handle, size.v);
        closePicker();
        showToast(true);
      });
      list.appendChild(option);
    });

    var skip = document.createElement('button');
    skip.type = 'button';
    skip.className = 'jnorss-wishlist-picker__skip';
    skip.textContent = 'Save without size';
    skip.addEventListener('click', function () {
      Wishlist.add(handle);
      closePicker();
      showToast(true);
    });

    picker.appendChild(head);
    picker.appendChild(list);
    picker.appendChild(skip);
    /* Nothing inside the picker may reach the product card (its overlay link / handlers). */
    picker.addEventListener('click', function (event) {
      event.stopPropagation();
    });

    pickerButton = button;
    host.appendChild(picker);
    document.addEventListener('keydown', onPickerKey);
    window.setTimeout(function () {
      document.addEventListener('click', onPickerOutside, true);
    }, 0);
    var first = list.querySelector('button:not(:disabled)') || close;
    first.focus({ preventScroll: true });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-jnorss-wishlist-toggle]');
    if (!button) return;
    /* The heart sits on top of the product card's link: never open the product. */
    event.preventDefault();
    event.stopPropagation();

    if (pickerClosedBy === button) {
      pickerClosedBy = null;
      return;
    }
    pickerClosedBy = null;

    var handle = button.getAttribute('data-jnorss-wishlist-toggle');
    if (Wishlist.has(handle)) {
      Wishlist.remove(handle);
      showToast(false);
      return;
    }

    var sizes = sizesFor(button);
    var preset = sizeSelectedOnPage(button, sizes);
    if (preset) {
      Wishlist.add(handle, preset);
      showToast(true);
      return;
    }
    if (sizes.length) {
      openPicker(button, handle, sizes);
      return;
    }
    Wishlist.add(handle);
    showToast(true);
  });

  /* Cart drawer / cart page: the heart beside each line's bin saves the product (with
     the size in the cart) and then presses the theme's own remove button, so the row
     animates out and the drawer, cart page and header counts update as they always do. */
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-jnorss-wishlist-from-cart]');
    if (!button || button.disabled) return;
    event.preventDefault();
    event.stopPropagation();

    var handle = button.getAttribute('data-jnorss-wishlist-from-cart');
    var size = button.getAttribute('data-jnorss-wishlist-size') || null;
    if (!handle) return;

    Wishlist.add(handle, size);
    button.disabled = true;
    showToast('moved');

    var row = button.closest('.cart-items__table-row');
    var remove = row && row.querySelector('.cart-items__remove:not([data-jnorss-wishlist-from-cart])');
    if (remove) remove.click();
  });

  /* Cards that render after load (SALE page template cards, infinite scroll,
     carousels re-rendered by the theme) get their heart state on arrival. */
  var observerTimer = 0;
  var observer = new MutationObserver(function (mutations) {
    var added = false;
    for (var i = 0; i < mutations.length && !added; i++) {
      var nodes = mutations[i].addedNodes;
      for (var k = 0; k < nodes.length; k++) {
        var node = nodes[k];
        if (node.nodeType !== 1) continue;
        if (node.hasAttribute('data-jnorss-wishlist-toggle') || node.querySelector('[data-jnorss-wishlist-toggle]')) {
          added = true;
          break;
        }
      }
    }
    if (!added) return;
    clearTimeout(observerTimer);
    observerTimer = setTimeout(function () {
      syncUI();
    }, 100);
  });

  document.addEventListener('jnorss:wishlist:change', function (event) {
    syncUI(event.detail.handles);
  });

  /* Another tab changed the list, or the page came back from the bfcache. */
  window.addEventListener('storage', function (event) {
    if (event.key === KEY) syncUI();
  });
  window.addEventListener('pageshow', function () {
    syncUI();
  });

  /* Cart ------------------------------------------------------------------------- */

  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      if (el.dataset.sectionId && ids.indexOf(el.dataset.sectionId) === -1) ids.push(el.dataset.sectionId);
    });
    return ids;
  }

  /* Adds `items` ([{ id, quantity }]) and tells the theme about it. Resolves to the
     cart's new item count, rejects with the message Shopify returned. */
  function addToCart(items, productId) {
    var routes = (window.Theme && window.Theme.routes) || {};
    var sections = cartSectionIds();
    var body = { items: items };
    if (sections.length) {
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }

    return fetch(routes.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (response) {
        return response.json().then(function (json) {
          if (!response.ok || json.status) {
            throw new Error(json.description || json.message || 'Could not add to cart');
          }
          return json;
        });
      })
      .then(function (added) {
        return fetch(routes.cart_url ? routes.cart_url + '.js' : '/cart.js', {
          headers: { Accept: 'application/json' },
        })
          .then(function (response) {
            return response.ok ? response.json() : null;
          })
          .catch(function () {
            return null;
          })
          .then(function (cart) {
            var itemCount = cart ? cart.item_count : items.length;
            var event = new Event('cart:update', { bubbles: true });
            event.detail = {
              resource: cart || {},
              sourceId: 'jnorss-wishlist',
              data: {
                source: 'jnorss-wishlist',
                itemCount: itemCount,
                productId: productId,
                variantId: items.length === 1 ? String(items[0].id) : undefined,
                sections: added.sections,
              },
            };
            document.dispatchEvent(event);
            return itemCount;
          });
      });
  }

  /* Wishlist page ---------------------------------------------------------------- */

  function formatMoney(cents, format) {
    var value = (Number(cents) || 0) / 100;
    var clean = String(format || '{{amount}}').replace(/<[^>]*>/g, '');
    var match = clean.match(/\{\{\s*(\w+)\s*\}\}/);
    var type = match ? match[1] : 'amount';
    var out;

    switch (type) {
      case 'amount_no_decimals':
        out = value.toLocaleString('en-US', { maximumFractionDigits: 0 });
        break;
      case 'amount_with_comma_separator':
        out = value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = value.toLocaleString('de-DE', { maximumFractionDigits: 0 });
        break;
      default:
        out = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    return match ? clean.replace(match[0], out) : out;
  }

  function formatDate(timestamp) {
    try {
      return new Date(timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) {
      return '';
    }
  }

  function sized(src, width) {
    try {
      var url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (e) {
      return src;
    }
  }

  /* The option that holds sizes, or the first option when none is called "size". */
  function sizeOption(product) {
    var options = product.options || [];
    if (!options.length) return null;
    var single = product.variants && product.variants.length === 1 && product.variants[0].title === 'Default Title';
    if (single) return null;
    var byName = options.filter(function (option) {
      return /size/i.test(option.name);
    })[0];
    return byName || options[0];
  }

  function variantsFor(product, option, value) {
    var index = (option.position || 1) - 1;
    return (product.variants || []).filter(function (variant) {
      var current = variant.options ? variant.options[index] : variant['option' + (index + 1)];
      return current === value;
    });
  }

  function pickVariant(list) {
    if (!list.length) return null;
    var available = list.filter(function (variant) {
      return variant.available;
    });
    return available[0] || list[0];
  }

  function initPage(root) {
    var grid = root.querySelector('[data-jnorss-wishlist-grid]');
    var template = root.querySelector('template[data-jnorss-wishlist-card]');
    var total = root.querySelector('[data-jnorss-wishlist-total]');
    var bulk = root.querySelector('[data-jnorss-wishlist-bulk]');
    var toolbar = root.querySelector('[data-jnorss-wishlist-toolbar]');
    var search = root.querySelector('[data-jnorss-wishlist-search]');
    var noMatch = root.querySelector('[data-jnorss-wishlist-nomatch]');
    var moneyFormat = root.getAttribute('data-money-format') || '{{amount}}';
    var text = {};
    try {
      text = JSON.parse(root.getAttribute('data-jnorss-wishlist-text') || '{}');
    } catch (e) {}
    var cache = {};
    var pass = 0;

    if (!grid || !template) return;

    /* Preview-only seeding: ?wishlist_demo=1 saves the sample products the section
       rendered into data-jnorss-wishlist-demo (only present on unpublished themes);
       ?wishlist_demo=clear empties the list. The parameter is then dropped from the URL. */
    (function seed() {
      var params = new URLSearchParams(window.location.search);
      var mode = params.get('wishlist_demo');
      if (!mode) return;
      if (mode === 'clear') {
        Wishlist.clear();
      } else if (root.dataset.jnorssWishlistDemo) {
        try {
          JSON.parse(root.dataset.jnorssWishlistDemo)
            .slice()
            .reverse()
            .forEach(function (handle) {
              if (!Wishlist.has(handle)) Wishlist.add(handle);
            });
        } catch (e) {}
      }
      params.delete('wishlist_demo');
      var query = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (query ? '?' + query : '') + window.location.hash);
    })();

    function fetchProduct(handle) {
      if (!cache[handle]) {
        cache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', {
          headers: { Accept: 'application/json' },
        })
          .then(function (response) {
            if (response.ok) return response.json();
            /* 404: the product is gone or unpublished, so drop it from the list. Any other
               failure keeps the handle, so a flaky connection never empties a wishlist. */
            return response.status === 404 ? { gone: true } : null;
          })
          .catch(function () {
            return null;
          });
      }
      return cache[handle];
    }

    function selectedCards() {
      return Array.prototype.filter.call(grid.querySelectorAll('[data-card]'), function (card) {
        var box = card.querySelector('[data-card-select]');
        return box && box.checked;
      });
    }

    function visibleCards() {
      return Array.prototype.slice.call(grid.querySelectorAll('[data-card]:not([hidden])'));
    }

    /* Search box: hide the cards whose title does not contain the typed text. */
    function applyFilter() {
      var query = search ? search.value.trim().toLowerCase() : '';
      var shown = 0;
      grid.querySelectorAll('[data-card]').forEach(function (card) {
        var title = (card.dataset.title || '').toLowerCase();
        var hit = !query || title.indexOf(query) !== -1;
        card.hidden = !hit;
        if (hit) shown++;
      });
      if (noMatch) noMatch.hidden = !(query && shown === 0 && grid.querySelector('[data-card]'));
      updateBulk();
    }

    function updateBulk() {
      if (!bulk) return;
      var cards = grid.querySelectorAll('[data-card]');
      var shown = visibleCards();
      var picked = selectedCards();
      var pickedShown = shown.filter(function (card) {
        return card.querySelector('[data-card-select]').checked;
      });
      var all = bulk.querySelector('[data-bulk-all]');
      var count = bulk.querySelector('[data-bulk-count]');
      var move = bulk.querySelector('[data-bulk-move]');
      var remove = bulk.querySelector('[data-bulk-remove]');
      var canMove = picked.filter(function (card) {
        return card.dataset.available === 'true';
      }).length;

      if (toolbar) toolbar.hidden = cards.length === 0;
      if (all) {
        /* "Select all" reflects the cards currently shown by the search box. */
        all.checked = shown.length > 0 && pickedShown.length === shown.length;
        all.indeterminate = pickedShown.length > 0 && pickedShown.length < shown.length;
        all.disabled = shown.length === 0;
      }
      if (count) count.textContent = picked.length ? picked.length + ' ' + (text.selected || 'selected') : '';
      if (move) move.disabled = canMove === 0;
      if (remove) remove.disabled = picked.length === 0;
    }

    function showError(card, message) {
      var error = card.querySelector('[data-card-error]');
      if (!error) return;
      error.textContent = message || text.move_error || 'Could not add this to the cart. Please try again.';
      error.hidden = false;
    }

    function setBusy(card, busy) {
      card.classList.toggle('is-busy', busy);
      var button = card.querySelector('[data-card-move]');
      if (button) button.disabled = busy || card.dataset.available !== 'true';
    }

    function applyVariant(card, product, variant) {
      var price = card.querySelector('[data-card-price]');
      var compare = card.querySelector('[data-card-compare]');
      var status = card.querySelector('[data-card-status]');
      var move = card.querySelector('[data-card-move]');
      var available = !!(variant && variant.available);

      card.dataset.variantId = variant ? String(variant.id) : '';
      card.dataset.available = available ? 'true' : 'false';

      var current = variant ? variant.price : product.price;
      var was = variant ? variant.compare_at_price : product.compare_at_price;
      if (price) price.textContent = formatMoney(current, moneyFormat);
      if (compare) {
        var onSale = was && was > current;
        compare.textContent = onSale ? formatMoney(was, moneyFormat) : '';
        compare.hidden = !onSale;
        card.classList.toggle('is-on-sale', !!onSale);
      }

      card.classList.toggle('is-sold-out', !available);
      if (status) status.hidden = available;
      if (move) {
        move.disabled = !available;
        move.textContent = available ? text.move || 'Move to cart' : text.sold_out || 'Sold out';
      }
      updateBulk();
    }

    function card(product, entry, index) {
      var node = template.content.firstElementChild.cloneNode(true);
      var url = product.url || '/products/' + product.handle;
      var image = node.querySelector('[data-card-image]');
      var src = product.featured_image || (product.images && product.images[0]) || '';

      node.dataset.handle = product.handle;
      node.dataset.productId = String(product.id);
      node.dataset.title = product.title || '';

      if (image) {
        if (src) {
          image.src = sized(src, 600);
          image.srcset = sized(src, 400) + ' 400w, ' + sized(src, 600) + ' 600w, ' + sized(src, 900) + ' 900w';
          image.sizes = '(min-width: 990px) 25vw, (min-width: 750px) 33vw, 50vw';
          image.alt = product.title || '';
          /* The first row is above the fold, so do not lazy-load it. */
          if (index < 4) image.loading = 'eager';
        } else {
          image.remove();
        }
      }

      node.querySelectorAll('[data-card-link]').forEach(function (link) {
        link.href = url;
      });
      node.querySelector('[data-card-title]').textContent = product.title || '';

      var vendor = node.querySelector('[data-card-vendor]');
      if (vendor) {
        vendor.textContent = product.vendor || '';
        vendor.hidden = !product.vendor;
      }

      var added = node.querySelector('[data-card-added]');
      if (added) {
        var when = entry && entry.addedAt ? formatDate(entry.addedAt) : '';
        added.textContent = when ? (text.added || 'Added on') + ' ' + when : '';
        added.hidden = !when;
      }

      /* Size picker */
      var option = sizeOption(product);
      var sizeRow = node.querySelector('[data-card-size-row]');
      var select = node.querySelector('[data-card-size]');
      var label = node.querySelector('[data-card-size-label]');
      var variant = null;

      if (option && select) {
        var id = 'jnorss-wishlist-size-' + product.handle;
        select.id = id;
        if (label) {
          label.htmlFor = id;
          label.textContent = /size/i.test(option.name) ? text.size || 'Size' : option.name;
        }
        select.setAttribute('aria-label', option.name);

        var firstAvailable = null;
        var values = /size/i.test(option.name) ? orderedValues(option) : option.values.slice();
        values.forEach(function (value) {
          var candidates = variantsFor(product, option, value);
          var pick = pickVariant(candidates);
          var opt = document.createElement('option');
          opt.value = value;
          opt.textContent = pick && pick.available ? value : value + ' – ' + (text.sold_out || 'Sold out');
          opt.disabled = !(pick && pick.available);
          if (!firstAvailable && pick && pick.available) firstAvailable = value;
          select.appendChild(opt);
        });

        /* The size picked when the heart was tapped wins; otherwise the first size in stock. */
        var saved = entry && entry.size && values.indexOf(entry.size) !== -1 ? entry.size : null;
        select.value = saved || firstAvailable || values[0];
        variant = pickVariant(variantsFor(product, option, select.value));

        select.addEventListener('change', function () {
          var error = node.querySelector('[data-card-error]');
          if (error) error.hidden = true;
          Wishlist.setSize(product.handle, select.value);
          applyVariant(node, product, pickVariant(variantsFor(product, option, select.value)));
        });
      } else {
        if (sizeRow) sizeRow.remove();
        variant = pickVariant(product.variants || []);
      }

      applyVariant(node, product, variant);

      /* Actions */
      var remove = node.querySelector('[data-card-remove]');
      if (remove) {
        remove.addEventListener('click', function () {
          Wishlist.remove(product.handle);
        });
      }

      var move = node.querySelector('[data-card-move]');
      if (move) {
        move.addEventListener('click', function () {
          var variantId = Number(node.dataset.variantId);
          if (!variantId) return;
          var error = node.querySelector('[data-card-error]');
          if (error) error.hidden = true;
          setBusy(node, true);
          addToCart([{ id: variantId, quantity: 1 }], String(product.id))
            .then(function () {
              Wishlist.remove(product.handle);
            })
            .catch(function (err) {
              setBusy(node, false);
              showError(node, err && err.message);
            });
        });
      }

      var box = node.querySelector('[data-card-select]');
      if (box) box.addEventListener('change', updateBulk);

      return node;
    }

    function render() {
      var entries = readEntries();
      var current = ++pass;

      if (!entries.length) {
        root.setAttribute('data-state', 'empty');
        grid.innerHTML = '';
        if (search) search.value = '';
        if (noMatch) noMatch.hidden = true;
        updateBulk();
        return;
      }

      if (root.getAttribute('data-state') !== 'items') root.setAttribute('data-state', 'loading');
      if (total) total.textContent = '(' + entries.length + ')';

      Promise.all(
        entries.map(function (entry) {
          return fetchProduct(entry.handle);
        })
      ).then(function (products) {
        if (current !== pass) return;

        /* Keep the shopper's ticks and size choices across re-renders. */
        var previous = {};
        grid.querySelectorAll('[data-card]').forEach(function (old) {
          var box = old.querySelector('[data-card-select]');
          var select = old.querySelector('[data-card-size]');
          previous[old.dataset.handle] = { checked: !!(box && box.checked), size: select ? select.value : null };
        });

        var fragment = document.createDocumentFragment();
        var keep = [];

        products.forEach(function (product, index) {
          if (product && product.gone) return;
          keep.push(entries[index]);
          if (!product) return;
          var node = card(product, entries[index], keep.length - 1);
          var memo = previous[product.handle];
          if (memo) {
            var box = node.querySelector('[data-card-select]');
            var select = node.querySelector('[data-card-size]');
            if (box) box.checked = memo.checked;
            if (select && memo.size && select.querySelector('option[value="' + memo.size.replace(/"/g, '\\"') + '"]')) {
              select.value = memo.size;
              select.dispatchEvent(new Event('change'));
            }
          }
          fragment.appendChild(node);
        });

        if (keep.length !== entries.length) {
          write(keep);
          return;
        }

        grid.innerHTML = '';
        grid.appendChild(fragment);
        if (total) total.textContent = '(' + keep.length + ')';
        root.setAttribute('data-state', keep.length ? 'items' : 'empty');
        applyFilter();
      });
    }

    if (search) {
      search.addEventListener('input', applyFilter);
      search.addEventListener('search', applyFilter);
    }

    /* List / grid view, remembered per browser. */
    var VIEW_KEY = 'jnorss_wishlist_view';
    var viewButtons = root.querySelectorAll('[data-jnorss-wishlist-view]');

    function setView(view, persist) {
      view = view === 'list' ? 'list' : 'grid';
      root.setAttribute('data-view', view);
      viewButtons.forEach(function (button) {
        button.setAttribute('aria-pressed', button.getAttribute('data-jnorss-wishlist-view') === view ? 'true' : 'false');
      });
      if (!persist) return;
      try {
        window.localStorage.setItem(VIEW_KEY, view);
      } catch (e) {}
    }

    var storedView = null;
    try {
      storedView = window.localStorage.getItem(VIEW_KEY);
    } catch (e) {}
    setView(storedView || root.getAttribute('data-view'), false);

    viewButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        setView(button.getAttribute('data-jnorss-wishlist-view'), true);
      });
    });

    /* Bulk bar */
    if (bulk) {
      var all = bulk.querySelector('[data-bulk-all]');
      var bulkMove = bulk.querySelector('[data-bulk-move]');
      var bulkRemove = bulk.querySelector('[data-bulk-remove]');

      if (all) {
        all.addEventListener('change', function () {
          visibleCards().forEach(function (card) {
            card.querySelector('[data-card-select]').checked = all.checked;
          });
          updateBulk();
        });
      }

      if (bulkRemove) {
        bulkRemove.addEventListener('click', function () {
          var handles = selectedCards().map(function (card) {
            return card.dataset.handle;
          });
          if (handles.length) Wishlist.remove(handles);
        });
      }

      if (bulkMove) {
        bulkMove.addEventListener('click', function () {
          var cards = selectedCards().filter(function (card) {
            return card.dataset.available === 'true' && Number(card.dataset.variantId);
          });
          if (!cards.length) return;
          bulkMove.disabled = true;
          cards.forEach(function (card) {
            setBusy(card, true);
          });
          addToCart(
            cards.map(function (card) {
              return { id: Number(card.dataset.variantId), quantity: 1 };
            }),
            undefined
          )
            .then(function () {
              Wishlist.remove(
                cards.map(function (card) {
                  return card.dataset.handle;
                })
              );
            })
            .catch(function (err) {
              cards.forEach(function (card) {
                setBusy(card, false);
                showError(card, err && err.message);
              });
              updateBulk();
            });
        });
      }
    }

    document.addEventListener('jnorss:wishlist:change', render);
    window.addEventListener('storage', function (event) {
      if (event.key === KEY) render();
    });
    render();
  }

  function init() {
    syncUI();
    observer.observe(document.body, { childList: true, subtree: true });
    document.querySelectorAll('[data-jnorss-wishlist-page]').forEach(initPage);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
